import { memo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setResolutions,
  setSubtitles,
  selectSelectedQuality,
  selectSelectedSubtitle,
  selectSelectedPlaybackSpeed,
} from "../../../app/global";
import { addBreadcrumb, captureError } from "../../../config/sentry";
// import Hls from "hls.js";

export default memo(function HlsPlayer({
  refVideo,
  url,
  loadVideo,
  timeUpdate,
  waiting,
  play,
  pause,
  error,
  streamEnd,
}) {
  const dispatch = useDispatch();
  const selectedQuality = useSelector(selectSelectedQuality);
  const selectedSubtitle = useSelector(selectSelectedSubtitle);
  const selectedPlaybackSpeed = useSelector(selectSelectedPlaybackSpeed);
  const currentTime = useRef(0);
  const hlsRef = useRef(null);

  useEffect(() => {
    let hls = null;

    addBreadcrumb("HLS instance creating", "player", { url });

    if (Hls.isSupported()) {
      hls = new Hls();
      hlsRef.current = hls;

      hls.loadSource(url);
      hls.attachMedia(refVideo.current);

      hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
        addBreadcrumb("HLS manifest parsed", "player", {
          levels: hls.levels?.length,
        });
        const resolutions = hls.levels.map((level) => level.height);
        const uniqueResolutions = [...new Set(resolutions)];
        const res = uniqueResolutions.map((resolution) => ({
          height: resolution,
          index: hls.levels.findIndex((level) => level.height === resolution),
          group_index: 0,
        }));
        dispatch(setResolutions(res));

        refVideo.current.play().catch((e) => {
          console.error("Play failed:", e);
          captureError(e, { context: "HLS play failed", url });
        });
      });

      hls.on(Hls.Events.SUBTITLE_TRACK_LOADED, function (e, data) {
        // dispatch(setSubtitles(hls.subtitleTracks));
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, function (e, data) {
        // console.log(data, "level switched");
      });

      hls.on(Hls.Events.ERROR, function (err, data) {
        addBreadcrumb("HLS error occurred", "player", {
          fatal: data.fatal,
          type: data.type,
        });
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.warn("network error, starting load");
              captureError(new Error("HLS Network Error"), {
                url,
                errorData: data,
              });
              // hls.startLoad();
              error(err);

              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.warn("media error, recovering media error");
              captureError(new Error("HLS Media Error"), {
                url,
                errorData: data,
              });
              error(err);
              // hls.recoverMediaError();
              break;
            default:
              console.warn("unrecoverable error, destroying hls", err, data);
              captureError(new Error("HLS Unrecoverable Error"), {
                url,
                errorData: data,
              });
              hls.destroy();
              break;
          }
        } else {
          // error(err);
        }
      });
    } else {
      refVideo.current.src = url;
    }

    return () => {
      if (hls) {
        addBreadcrumb("HLS instance destroying", "player", { url });
        hls.stopLoad();
        hls.detachMedia();
        console.warn("destroy");
        hls.destroy();
      }
    };
  }, [url]);

  useEffect(() => {
    if (hlsRef.current) {
      const hls = hlsRef.current;
      if (selectedQuality.id === "Auto") {
        hls.currentLevel = -1; // Auto quality
      } else {
        const qualityLevel = hls.levels.findIndex(
          (level) => `${level.height}p` === selectedQuality.id,
        );
        if (qualityLevel !== -1) {
          hls.currentLevel = qualityLevel;
        }
      }
    }
  }, [selectedQuality]);

  useEffect(() => {
    if (hlsRef.current) {
      const hls = hlsRef.current;
      if (selectedSubtitle.id === "Off") {
        hls.subtitleTrack = -1;
      } else {
        const subtitleTrack = hls.subtitleTracks.findIndex(
          (track) => track.id === selectedSubtitle.id,
        );
        if (subtitleTrack !== -1) {
          hls.subtitleTrack = subtitleTrack;
        }
      }
    }
  }, [selectedSubtitle]);

  useEffect(() => {
    if (refVideo.current) {
      refVideo.current.playbackRate = 1;
    }
  }, [selectedPlaybackSpeed]);

  return (
    <video
      onTimeUpdate={() => {
        if (currentTime.current !== Math.floor(refVideo.current?.currentTime)) {
          currentTime.current = Math.floor(refVideo.current?.currentTime);
          timeUpdate(refVideo.current?.currentTime, refVideo.current?.duration);
        }
      }}
      onEnded={streamEnd}
      onLoadedMetadata={loadVideo}
      onError={error}
      onWaiting={waiting}
      id="video_player"
      ref={refVideo}
      autoPlay={true}
      src={url}
      playsInline
    />
  );
});
